import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Home as HomeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RocketLaunch as RocketLaunchIcon,
  Extension as ExtensionIcon,
  Folder as FolderIcon,
  FiberManualRecord as RecordIcon,
  Edit as EditIcon,
  PlayCircle as PlayCircleIcon,
  ViewList as ViewListIcon,
  AutoFixHigh as AutoFixHighIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Keyboard as KeyboardIcon,
  MenuBook as MenuBookIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Terminal as TerminalIcon,
} from "@mui/icons-material";
import { PipelineSetup } from "../components/ApiTokens";

const PRIMARY = "#4f46e5";
const PRIMARY_DARK = "#3730a3";
const PRIMARY_LIGHT = alpha(PRIMARY, 0.1);
const PRIMARY_MID = "#c7d2fe";
const EMERALD = "#10b981";
const EMERALD_LIGHT = "#d1fae5";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const VIOLET = "#7c3aed";

const CHECKLIST_KEY = "testify_help_checklist";
const SECTION_KEY = "testify_help_section";

const SECTIONS = [
  { id: "welcome", label: "Welcome", group: "Getting Started", icon: <HomeIcon fontSize="small" /> },
  { id: "checklist", label: "Onboarding Checklist", group: "Getting Started", icon: <CheckCircleOutlineIcon fontSize="small" />, badge: "Start here", badgeColor: "success" },
  { id: "quickstart", label: "Quick Start Guide", group: "Getting Started", icon: <RocketLaunchIcon fontSize="small" /> },
  { id: "extension", label: "Chrome Extension", group: "Getting Started", icon: <ExtensionIcon fontSize="small" /> },
  { id: "projects", label: "Projects & Folders", group: "Core Features", icon: <FolderIcon fontSize="small" /> },
  { id: "recording", label: "Recording Tests", group: "Core Features", icon: <RecordIcon fontSize="small" /> },
  { id: "editor", label: "Test Editor", group: "Core Features", icon: <EditIcon fontSize="small" /> },
  { id: "execution", label: "Running Tests", group: "Core Features", icon: <PlayCircleIcon fontSize="small" /> },
  { id: "suites", label: "Test Suites", group: "Core Features", icon: <ViewListIcon fontSize="small" /> },
  { id: "healing", label: "Self-Healing AI", group: "Advanced", icon: <AutoFixHighIcon fontSize="small" />, badge: "AI", badgeColor: "secondary" },
  { id: "datadriven", label: "Data-Driven Tests", group: "Advanced", icon: <StorageIcon fontSize="small" /> },
  { id: "scheduling", label: "Scheduling", group: "Advanced", icon: <ScheduleIcon fontSize="small" /> },
  { id: "cicd", label: "CI/CD Integration", group: "Advanced", icon: <TerminalIcon fontSize="small" /> },
  { id: "analytics", label: "Analytics", group: "Advanced", icon: <AnalyticsIcon fontSize="small" /> },
  { id: "search", label: "Search", group: "Reference", icon: <SearchIcon fontSize="small" /> },
  { id: "workspace", label: "Workspace & Auth", group: "Reference", icon: <BusinessIcon fontSize="small" /> },
  { id: "shortcuts", label: "Keyboard Shortcuts", group: "Reference", icon: <KeyboardIcon fontSize="small" /> },
  { id: "glossary", label: "Glossary", group: "Reference", icon: <MenuBookIcon fontSize="small" /> },
  { id: "faq", label: "FAQ", group: "Reference", icon: <HelpOutlineIcon fontSize="small" /> },
];

/* ─── Reusable sub-components ─────────────────────────────── */

function SectionHeader({ eyebrow, title, desc }) {
  return (
    <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", color: PRIMARY, mb: 0.75 }}>
        {eyebrow}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.75, color: "text.primary" }}>{title}</Typography>
      {desc && <Typography sx={{ color: "text.secondary", fontSize: 14, maxWidth: 620 }}>{desc}</Typography>}
    </Box>
  );
}

function Callout({ type = "tip", icon, children }) {
  const styles = {
    tip:     { border: PRIMARY   },
    warning: { border: AMBER     },
    success: { border: EMERALD   },
    info:    { border: "#0ea5e9" },
  };
  const s = styles[type] || styles.tip;
  return (
    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", borderRadius: 1, px: 2, py: 1.5, my: 1.75, bgcolor: alpha(s.border, 0.1), borderLeft: `3px solid ${s.border}` }}>
      {icon && <Typography sx={{ fontSize: 15, flexShrink: 0, mt: 0.25 }}>{icon}</Typography>}
      <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.65, "& strong": { fontWeight: 700 } }}>{children}</Typography>
    </Box>
  );
}

function Steps({ children }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 0, my: 2 }}>{children}</Box>;
}

function Step({ num, title, children }) {
  return (
    <Box sx={{ display: "flex", gap: 1.75, position: "relative", "&:not(:last-child)::before": { content: '""', position: "absolute", left: "16px", top: 36, width: 2, height: "calc(100% - 8px)", bgcolor: "divider" } }}>
      <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: PRIMARY, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0, zIndex: 1 }}>
        {num}
      </Box>
      <Box sx={{ pb: 3, flex: 1 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.5, mt: 0.5, color: "text.primary" }}>{title}</Typography>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.65 }}>{children}</Typography>
      </Box>
    </Box>
  );
}

function FeatureGrid({ children }) {
  return <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.75, my: 2 }}>{children}</Box>;
}

function FeatureCard({ title, children, accent }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 1.5, borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.6 }}>{children}</Typography>
    </Paper>
  );
}

function StatusBadge({ status }) {
  const map = {
    passed:   { bg: EMERALD_LIGHT,  color: "#065f46" },
    failed:   { bg: "#fee2e2",      color: "#991b1b" },
    running:  { bg: "#eff6ff",      color: "#1e40af" },
    pending:  { bg: "#f1f5f9",      color: "#475569" },
    draft:    { bg: "#f1f5f9",      color: "#64748b" },
    active:   { bg: EMERALD_LIGHT,  color: "#065f46" },
    archived: { bg: "#f1f5f9",      color: "#64748b" },
    healing:  { bg: "#fef3c7",      color: "#92400e" },
    rejected: { bg: "#fee2e2",      color: "#991b1b" },
  };
  const s = map[status.toLowerCase()] || map.pending;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: 0.25, borderRadius: 20, fontSize: 11.5, fontWeight: 600, bgcolor: s.bg, color: s.color }}>
      {status.toUpperCase()}
    </Box>
  );
}

function Kbd({ children }) {
  return (
    <Box component="kbd" sx={{ bgcolor: "action.hover", border: 1, borderColor: "divider", borderRadius: "4px", px: 0.875, py: 0.25, fontSize: 11.5, fontFamily: "inherit", fontWeight: 600 }}>
      {children}
    </Box>
  );
}

function Code({ children }) {
  return (
    <Box component="code" sx={{ bgcolor: "action.hover", px: 0.75, py: 0.125, borderRadius: "4px", fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
      {children}
    </Box>
  );
}

function MockupFrame({ title, children }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 16px rgba(15,23,42,.1)", my: 2.25 }}>
      <Box sx={{ bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", px: 1.75, py: 1.125, display: "flex", alignItems: "center", gap: 0.875 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#ef4444" }} />
        <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#f59e0b" }} />
        <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#22c55e" }} />
        <Box sx={{ flex: 1, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "5px", px: 1.375, py: 0.375, fontSize: 11.5, color: "text.secondary", mx: 1.25 }}>
          {title || "Testify"}
        </Box>
      </Box>
      <Box sx={{ p: 2.25, bgcolor: "background.paper" }}>{children}</Box>
    </Paper>
  );
}

function DocTable({ headers, rows }) {
  return (
    <Table size="small" sx={{ my: 1.75, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1.5, overflow: "hidden", "& .MuiTableCell-root": { fontSize: 13, py: 1, px: 1.625 }, "& .MuiTableHead-root .MuiTableCell-root": { bgcolor: "action.hover", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".05em", color: "text.secondary" } }}>
      <TableHead>
        <TableRow>{headers.map((h, i) => <TableCell key={i}>{h}</TableCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, ri) => (
          <TableRow key={ri} sx={{ "&:last-child td": { border: 0 } }}>
            {row.map((cell, ci) => <TableCell key={ci}>{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* ─── Section content ─────────────────────────────────────── */

function WelcomeSection({ onNavigate }) {
  return (
    <Box>
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #6366f1 50%, ${VIOLET} 100%)`, color: "white", p: "44px 40px", borderRadius: 2, mb: 4, position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "rgba(255,255,255,.06)", borderRadius: "50%" } }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", opacity: .7, mb: 1 }}>Documentation</Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>Welcome to Testify 👋</Typography>
        <Typography sx={{ fontSize: "1.05rem", opacity: .88, maxWidth: 520, lineHeight: 1.7 }}>
          Everything you need to automate browser tests without writing code — from recording your first test to scheduling AI-healed suite runs at scale.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2.75 }}>
          {["🎥 No-code recording", "🤖 AI self-healing", "📊 Data-driven tests", "🕐 Scheduling", "📈 Analytics", "🏢 Multi-workspace"].map(c => (
            <Box key={c} sx={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", color: "white", px: 1.625, py: 0.5, borderRadius: 20, fontSize: 12.5, fontWeight: 500 }}>{c}</Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.75, mb: 4 }}>
        {[
          { icon: "✅", title: "New to Testify?", desc: "Work through the onboarding checklist and record your first passing test in under 10 minutes.", link: "checklist", cta: "Start here →" },
          { icon: "⏺", title: "Record a Test", desc: "Use the Chrome extension to capture every click and keystroke — no code required.", link: "recording", cta: "Learn more →" },
          { icon: "🔧", title: "Self-Healing AI", desc: "Testify automatically detects when UI changes break selectors and proposes one-click fixes.", link: "healing", cta: "See how it works →" },
        ].map(c => (
          <Paper key={c.link} variant="outlined" onClick={() => onNavigate(c.link)} sx={{ p: 2.75, borderRadius: 2, cursor: "pointer", transition: "all .15s", "&:hover": { borderColor: PRIMARY, boxShadow: "0 4px 16px rgba(15,23,42,.1)", transform: "translateY(-2px)" } }}>
            <Typography sx={{ fontSize: 26, mb: 1.25 }}>{c.icon}</Typography>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.625 }}>{c.title}</Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.55, mb: 1.5 }}>{c.desc}</Typography>
            <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 12.5, fontWeight: 600, color: PRIMARY }}>{c.cta}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, p: "24px 28px", borderRadius: 2, mb: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "#94a3b8", width: "100%", mb: 2 }}>HOW TESTIFY WORKS</Typography>
        {[
          { bg: PRIMARY_LIGHT, border: PRIMARY_MID, icon: "🧩", label: "Chrome Extension", sub: "Records clicks & keystrokes", color: PRIMARY },
          { bg: "#f0fdf4",      border: "#bbf7d0",  icon: "✏️", label: "Test Editor",       sub: "Review & edit steps",       color: "#15803d" },
          { bg: "#fff7ed",      border: "#fed7aa",  icon: "▶️", label: "Playwright Executor", sub: "Runs in real browser",    color: "#c2410c" },
          { bg: "#fdf4ff",      border: "#e9d5ff",  icon: "🔧", label: "AI Self-Healer",    sub: "Auto-fixes broken selectors", color: VIOLET },
          { bg: EMERALD_LIGHT,  border: "#6ee7b7",  icon: "📈", label: "Analytics",          sub: "Trends & reports",         color: "#065f46" },
        ].map((n, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            {i > 0 && <Typography sx={{ color: "text.disabled", fontSize: 18, flexShrink: 0 }}>→</Typography>}
            <Box sx={{ textAlign: "center", flex: 1, p: "14px 18px", borderRadius: 1.25, bgcolor: n.bg, border: `1px solid ${n.border}` }}>
              <Typography sx={{ fontSize: 22, mb: 0.625 }}>{n.icon}</Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: n.color }}>{n.label}</Typography>
              <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.375 }}>{n.sub}</Typography>
            </Box>
          </Box>
        ))}
      </Paper>

      <FeatureGrid>
        <FeatureCard title="🎯 For QA Engineers">Replace brittle Selenium/Cypress suites. Spend time writing coverage, not fixing selectors after every deploy.</FeatureCard>
        <FeatureCard title="👩‍💼 For Product Teams">Non-technical team members — PMs, analysts — can record and maintain tests without touching any code.</FeatureCard>
        <FeatureCard title="⚡ Playwright-Powered">All tests run on real Chromium via Playwright with live step-by-step streaming back to the UI.</FeatureCard>
        <FeatureCard title="🏢 Multi-Workspace">Separate environments for different teams or products. All data is workspace-scoped for clean isolation.</FeatureCard>
      </FeatureGrid>
    </Box>
  );
}

function ChecklistSection({ checkedItems, onToggle }) {
  const ITEMS = [
    { id: "install_ext",   label: "Install the Chrome Extension",          link: "extension" },
    { id: "create_proj",   label: "Create your first Project",             link: "projects"  },
    { id: "record_test",   label: "Record a Test using the extension",     link: "recording" },
    { id: "run_test",      label: "Run the test and see it pass",          link: "execution" },
    { id: "add_assertion", label: "Add an assertion step to a test",       link: "recording" },
    { id: "edit_step",     label: "Edit a recorded step in the Test Editor", link: "editor"  },
    { id: "create_suite",  label: "Create a Test Suite and run it",        link: "suites"    },
    { id: "data_driven",   label: "Try a data-driven test run with a CSV", link: "datadriven"},
    { id: "healing",       label: "Review a Self-Healing AI suggestion",   link: "healing"   },
    { id: "schedule",      label: "Schedule a suite to run nightly",       link: "scheduling"},
  ];
  const count = Math.min(checkedItems.length, ITEMS.length);
  const pct = Math.round((count / ITEMS.length) * 100);
  const circ = 2 * Math.PI * 23;
  const theme = useTheme();

  return (
    <Box>
      <SectionHeader eyebrow="Getting Started" title="Onboarding Checklist" desc="Ten milestones from zero to confident Testify user. Check each one off as you complete it — your progress is saved in the browser." />

      <Paper variant="outlined" sx={{ p: "22px 24px", borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
          <Box>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, mb: 0.375 }}>Your Progress</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{count} of {ITEMS.length} complete</Typography>
          </Box>
          <Box sx={{ position: "relative", width: 56, height: 56, flexShrink: 0 }}>
            <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: "rotate(-90deg)" }}>
              <circle fill="none" stroke={theme.palette.divider} strokeWidth="5" cx="28" cy="28" r="23" />
              <circle fill="none" stroke={EMERALD} strokeWidth="5" strokeLinecap="round" cx="28" cy="28" r="23" strokeDasharray={circ} strokeDashoffset={circ - (circ * pct / 100)} style={{ transition: "stroke-dashoffset .5s" }} />
            </svg>
            <Typography sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: PRIMARY }}>{pct}%</Typography>
          </Box>
        </Box>
        <Box sx={{ bgcolor: "action.hover", borderRadius: 0.75, height: 7, overflow: "hidden" }}>
          <Box sx={{ height: "100%", bgcolor: EMERALD, borderRadius: 0.75, width: `${pct}%`, transition: "width .4s" }} />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {ITEMS.map(item => {
          const done = checkedItems.includes(item.id);
          return (
            <Box key={item.id} onClick={() => onToggle(item.id)} sx={{ display: "flex", alignItems: "center", gap: 1.625, p: "13px 16px", bgcolor: done ? EMERALD_LIGHT : "background.paper", border: 1, borderColor: done ? EMERALD : "divider", borderRadius: 1.25, cursor: "pointer", transition: "all .15s", "&:hover": { borderColor: done ? EMERALD : PRIMARY, bgcolor: done ? EMERALD_LIGHT : PRIMARY_LIGHT } }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "50%", border: 2, borderColor: done ? EMERALD : "text.disabled", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, bgcolor: done ? EMERALD : "transparent", color: "white", fontSize: 11, fontWeight: 700, transition: "all .2s" }}>
                {done && "✓"}
              </Box>
              <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: done ? "#065f46" : "text.primary", textDecoration: done ? "line-through" : "none" }}>{item.label}</Typography>
              <Typography sx={{ fontSize: 12, color: PRIMARY, fontWeight: 600, flexShrink: 0 }}>View docs →</Typography>
            </Box>
          );
        })}
      </Box>

      {count >= ITEMS.length && (
        <Callout type="success" icon="🎉">
          <strong>Onboarding complete!</strong> You're a Testify power user. Explore data-driven testing, analytics, and scheduling to take it further.
        </Callout>
      )}
    </Box>
  );
}

function QuickStartSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Getting Started" title="Quick Start Guide" desc="Record your first passing test in under 10 minutes. Follow these five steps in order." />
      <Callout type="tip" icon="💡"><strong>Demo credentials:</strong> Log in with <Code>demo@testify.com</Code> / <Code>demo123</Code> to explore with pre-loaded sample data — no setup required.</Callout>
      <Steps>
        <Step num={1} title="Install the Chrome Extension">
          Go to <Code>chrome://extensions</Code>, toggle <strong>Developer mode</strong> on, click <strong>Load unpacked</strong>, and select the <Code>extension/</Code> folder from your Testify directory. The Testify icon appears in your toolbar.
          <Callout type="warning" icon="⚠️">The recorder bridge only activates on <Code>localhost:3000</Code> and <Code>*.testify.com</Code> tabs. Keep the Testify app tab open when recording.</Callout>
        </Step>
        <Step num={2} title="Create a Project">
          In the sidebar click <strong>Projects → New Project</strong>. Give it a name like "My Web App". Projects are containers for all your tests and folders — you can have multiple projects for different apps or teams.
        </Step>
        <Step num={3} title="Create a New Test">
          Inside the project click <strong>New Test</strong>. Enter a name like <em>"User Login Flow"</em>. You'll land on the Test Editor (<Code>/tests/new</Code>).
        </Step>
        <Step num={4} title="Record Your Steps">
          On the <strong>Tests</strong> page click <strong>Record Test</strong>, enter a name and start URL, then click <strong>Start Recording</strong>. A new window opens on that URL — interact normally: click buttons, fill forms, navigate pages. Every action is captured automatically. Right-click any element to add an assertion. Return to Testify and click <strong>Stop Recording</strong>, then <strong>Save Test</strong>.
        </Step>
        <Step num={5} title="Run Your Test">
          Click the green <strong>Run Test</strong> button. Testify opens a real browser, executes each step, and streams live results. A <StatusBadge status="passed" /> badge means your test is working perfectly.
          <Callout type="success" icon="🎉"><strong>Done!</strong> Your test is saved and can be re-run any time, added to a suite, or scheduled automatically.</Callout>
        </Step>
      </Steps>
    </Box>
  );
}

function ExtensionSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Setup" title="Chrome Extension" desc="The Testify extension is the bridge between your browser interactions and the test editor. It captures DOM events and routes them to the Testify app via window.postMessage." />
      <FeatureGrid>
        <FeatureCard title="📡 Event Recording">Captures clicks, inputs, navigation events and form submissions in real time via <Code>content.js</Code> injected into the page.</FeatureCard>
        <FeatureCard title="🖱 Right-Click Assertions">Right-click any element on your app while recording to add assertion steps: check text, element visibility, or attribute values.</FeatureCard>
        <FeatureCard title="🔗 Secure Bridge">Uses <Code>window.postMessage</Code> to securely send recorded steps to the open Testify tab. Your auth token never leaves the Testify page.</FeatureCard>
        <FeatureCard title="🔒 Scoped Activation">The recorder bridge only activates on <Code>localhost:3000</Code> and <Code>*.testify.com</Code> — prevents accidental recording on other sites.</FeatureCard>
      </FeatureGrid>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>How to install</Typography>
      <Steps>
        <Step num={1} title="Open Chrome Extensions">Navigate to <Code>chrome://extensions</Code> in your Chrome address bar.</Step>
        <Step num={2} title="Enable Developer Mode">Toggle <strong>Developer mode</strong> on using the switch in the top-right corner of the extensions page.</Step>
        <Step num={3} title="Load the Extension">Click <strong>Load unpacked</strong> and navigate to the <Code>extension/</Code> folder in your Testify project directory. Click <strong>Select Folder</strong>.</Step>
        <Step num={4} title="Pin the Extension">Click the puzzle-piece icon in Chrome's toolbar, find Testify, and click the pin icon so it's always visible.</Step>
        <Step num={5} title="Verify it's working">Open the Testify app, go to <strong>Tests</strong>, click <strong>Record Test</strong>, and click <strong>Start Recording</strong>. The recording window shows a pulsing red dot in its top-right corner, and the extension popup shows <strong>Recording</strong>.</Step>
      </Steps>
      <Callout type="tip" icon="💡"><strong>Tip:</strong> Use a dedicated Chrome profile for Testify testing with no other extensions active. This keeps recordings clean and prevents interference with DOM event capture.</Callout>
      <Callout type="warning" icon="⚠️"><strong>No build step required.</strong> The extension is plain JavaScript (Manifest V3). Simply load it unpacked — no npm install needed.</Callout>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>What the extension captures</Typography>
      <DocTable headers={["Action", "Example", "What's captured"]} rows={[
        ["🖱 Click",         "Click a button or link",     "CSS selector, element text, coordinates"],
        ["⌨️ Type",          "Fill a form field",           "Selector, typed value, field label"],
        ["🔗 Navigate",      "Browser changes URL",         "Target URL, page title"],
        ["📋 Select",        "Choose a dropdown option",    "Selector, selected value"],
        ["🔲 Check/Uncheck", "Toggle a checkbox",           "Selector, checked state"],
        ["✅ Assertion",     "Right-click → Assert text",   "Selector, expected value, assertion type"],
      ]} />
    </Box>
  );
}

function ProjectsSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Core Feature" title="Projects & Folders" desc="Testify uses a two-level hierarchy: Projects contain Folders, and folders contain Tests. Organize your suite by application, team, or feature." />
      <FeatureGrid>
        <FeatureCard title="📁 Projects">Top-level containers. Each project maps to one application or product. Projects have separate analytics and can be workspace-scoped.</FeatureCard>
        <FeatureCard title="📂 Folders">Organizational units within a project. Group tests by feature, sprint, or criticality.</FeatureCard>
        <FeatureCard title="🏷 Test Lifecycle">Tests have three states: <StatusBadge status="draft" /> while editing, <StatusBadge status="active" /> when published, <StatusBadge status="archived" /> when retired.</FeatureCard>
        <FeatureCard title="👥 Who Sees What">By default you see only the projects, tests, suites and data sets you created. An admin sees everything. If an admin or super user turns on your <strong>Data Visibility</strong>, you can also view and run everyone else's, but only the owner or an admin can change them.</FeatureCard>
        <FeatureCard title="📄 View Only &amp; Clone">Someone else's item shows <strong>Owner</strong> and <strong>View only</strong>: you can open it and run it, not edit it. Use <strong>Clone</strong> to make your own copy in one of your projects. What it uses that you don't own (a suite's tests, a test's data set) is copied too.</FeatureCard>
        <FeatureCard title="🛡 Admins &amp; Super Users">Admins manage all accounts and roles in Settings → Users. Super users can add regular users, block or unblock them and switch their data visibility, but can't change roles or touch admins and other super users.</FeatureCard>
        <FeatureCard title="🔍 Quick Find">Press <Kbd>/</Kbd> anywhere to search across all projects, folders, and tests instantly.</FeatureCard>
      </FeatureGrid>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Organization best practices</Typography>
      <DocTable headers={["Pattern", "When to use", "Example"]} rows={[
        ["By feature",      "Small–medium apps with clear feature boundaries", "Auth, Checkout, Profile, Admin"],
        ["By criticality",  "When you need tiered CI gates",                   "Smoke Tests, Full Regression, Edge Cases"],
        ["By team",         "Multiple squads sharing one project",             "Payments Team, Platform Team"],
        ["By sprint",       "Tracking test coverage per release",              "Sprint 42, Sprint 43"],
      ]} />
    </Box>
  );
}

function RecordingSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Core Feature" title="Recording Tests" desc="Testify's recorder captures every interaction with your web app and converts it into replayable test steps. No code, no selectors to write manually." />
      <DocTable headers={["Action", "Example", "What Testify captures"]} rows={[
        ["🖱 Click",         "Click a button or link",     "Element selector, coordinates, element text"],
        ["⌨️ Type",          "Fill a form field",           "Selector, typed value, field label"],
        ["🔗 Navigate",      "Browser navigates to a URL", "Target URL, page title"],
        ["📋 Select",        "Choose dropdown option",      "Selector, selected value"],
        ["🔲 Check/Uncheck", "Toggle a checkbox",           "Selector, checked state"],
        ["✅ Assertion",     "Right-click → Assert text",   "Selector, expected value, assertion type"],
      ]} />
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Adding assertions while recording</Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1.75 }}>Right-click any element during recording to access the assertion context menu:</Typography>
      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 1.5, maxWidth: 320, mb: 2 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#94a3b8", mb: 1.125, textTransform: "uppercase", letterSpacing: ".06em" }}>Context Menu (right-click)</Typography>
        {[["✅", "Assert this text is visible"], ["👁", "Assert element is visible"], ["🚫", "Assert element is not visible"], ["🔢", "Assert element count"], ["📝", "Assert attribute value"]].map(([icon, label]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.125, py: 0.875, borderBottom: 1, borderColor: "divider", "&:last-child": { borderBottom: 0 } }}>
            <Typography>{icon}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{label}</Typography>
          </Box>
        ))}
      </Paper>
      <Callout type="tip" icon="💡"><strong>Pro tip:</strong> Add at least one assertion per meaningful state change. For a login flow, assert the dashboard heading is visible <em>after</em> clicking Log In — this proves the login actually succeeded, not just that the click ran.</Callout>
      <FeatureGrid>
        <FeatureCard title="✅ Do" accent={EMERALD}>Move slowly and deliberately. Add assertions at key checkpoints. Name your test before recording. Use realistic test data.</FeatureCard>
        <FeatureCard title="❌ Don't" accent={RED}>Resize the browser mid-recording. Use browser back/forward unexpectedly. Open DevTools (it can interfere with event capture).</FeatureCard>
        <FeatureCard title="🗂 Several Tabs">Open new tabs in the recording window and switch between them freely: the recording gets <strong>Open a new tab</strong> and <strong>Switch to tab N</strong> steps. Pasting a link the app copied (like a share link) is recorded as <strong>Open the copied link</strong>, so each run opens the link it just copied.</FeatureCard>
      </FeatureGrid>
    </Box>
  );
}

function EditorSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Core Feature" title="Test Editor" desc="After recording, the Test Editor lets you view, reorder, edit, and augment your steps. Handles both creating new tests (/tests/new) and editing existing ones (/tests/:id)." />
      <FeatureGrid>
        <FeatureCard title="↕️ Drag to Reorder">Drag the grip handle on any step to reorder it. Steps execute in the order shown.</FeatureCard>
        <FeatureCard title="✏️ Edit Inline">Click the edit icon on any step to change its selector, value, or action type — useful when the recorder captured a selector you want to simplify.</FeatureCard>
        <FeatureCard title="➕ Add Manual Steps">Click "Add step manually" to insert custom steps for actions hard to record, like waiting for a network response.</FeatureCard>
        <FeatureCard title="📦 Variable Syntax">Use <Code>{"{{variableName}}"}</Code> in any step value to inject runtime data from datasets. Enables data-driven tests from the same editor.</FeatureCard>
        <FeatureCard title="☑️ Select Several Steps">Tick the checkboxes (or the one next to "Test Steps" for all) to move, duplicate, enable, disable or delete steps together. Dragging a selected step moves the whole selection.</FeatureCard>
        <FeatureCard title="👁 Disable a Step">The eye icon turns a step off without deleting it. Disabled steps are skipped when the test runs and show as <strong>Skipped</strong> in the results.</FeatureCard>
      </FeatureGrid>

      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1 }}>Editing a recording</Typography>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.65 }}>
        Open a step's <strong>⋮</strong> menu to continue a recording from that point. The extension opens a browser window, <strong>replays the steps before it</strong> so the page is in the right state, then records.
      </Typography>
      <DocTable headers={["Action", "Replays", "Result"]} rows={[
        ["Record after this step – keep the rest",    "Up to this step", "New steps are inserted after it; later steps stay"],
        ["Record after this step – replace the rest", "Up to this step", "New steps replace every step after it"],
        ["Re-record this step",                        "Steps before it", "Your next action replaces this step"],
        ["Pick element for this step",                 "Steps before it", "Click an element to retarget the step (the click isn't performed)"],
        ["Record more (above the list)",               "The whole test",  "New steps are added at the end"],
      ]} />
      <Callout type="warning" icon="⚠️">
        Replay performs the steps <strong>for real</strong>, like a test run: records get created and forms get sent. For a data-driven test, choose which data row fills the <Code>{"{{variables}}"}</Code> during replay.
      </Callout>
      <Callout type="tip" icon="⏸">
        If a step can't be replayed, replay pauses. You can <strong>Retry</strong>, do the step yourself in the window and choose <strong>I did it manually, continue</strong>, <strong>Skip</strong> it, or <strong>Start recording from here</strong> to replace it and the rest of the replayed steps with a new recording.
      </Callout>
      <Callout type="info" icon="💾">
        Recorded and re-targeted steps are marked <strong>New</strong> or <strong>Changed</strong> and aren't stored until you click <strong>Save</strong>. Chrome shows "Testify Recorder started debugging this browser" while steps replay; cancelling that bar stops the replay.
      </Callout>
      <Callout type="tip" icon="🎯">
        Changing a step's selector by hand discards the locators recorded for the old element, so self-healing can't find the old element again. The step then shows <strong>No fallback locators</strong>; click it to pick the element in the browser and record a fresh set.
      </Callout>

      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Test lifecycle states</Typography>
      <DocTable headers={["Status", "Meaning", "Can be run?"]} rows={[
        [<StatusBadge status="draft" />,    "Work in progress — not yet published",         "Yes (manually)"],
        [<StatusBadge status="active" />,   "Published and available for suites/schedules", "Yes"],
        [<StatusBadge status="archived" />, "Retired — hidden from active views",           "No"],
      ]} />
    </Box>
  );
}

function ExecutionSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Core Feature" title="Running Tests" desc="Testify executes tests using a real Playwright browser, streaming live step-by-step progress back to the UI via Socket.io." />
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, flexWrap: "wrap", mb: 3 }}>
        {[
          { bg: PRIMARY_LIGHT, icon: "▶️", label: "1. Click Run",       sub: "UI calls executor" },
          { bg: "#fff7ed",     icon: "🎭", label: "2. Playwright Runs", sub: "Real Chromium browser" },
          { bg: EMERALD_LIGHT, icon: "📡", label: "3. Live Streaming",  sub: "Socket.io step events" },
          { bg: "#fdf4ff",     icon: "💾", label: "4. Result Saved",    sub: "Backend persists history" },
        ].map((n, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 90 }}>
            {i > 0 && <Typography sx={{ color: "text.disabled", fontSize: 18 }}>→</Typography>}
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Box sx={{ width: 46, height: 46, bgcolor: n.bg, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, mx: "auto", mb: 0.875 }}>{n.icon}</Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{n.label}</Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{n.sub}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <MockupFrame title="Testify — Execution Results">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.375, mb: 2.25, pb: 1.75, borderBottom: 1, borderColor: "divider" }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>User Login Flow</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Ran 2 minutes ago · 4 steps · 1.4s</Typography>
          </Box>
          <Box sx={{ ml: "auto" }}><StatusBadge status="passed" /></Box>
        </Box>
        {["Navigate to https://app.example.com/login", 'Type "test@example.com" into #email', 'Click "Log In" button', 'Assert "Welcome back!" is visible'].map(s => (
          <Box key={s} sx={{ display: "flex", alignItems: "center", gap: 1.125, p: "9px 13px", bgcolor: alpha(EMERALD, 0.1), borderRadius: 1, border: `1px solid ${alpha(EMERALD, 0.35)}`, mb: 0.875 }}>
            <Typography sx={{ color: EMERALD, fontSize: 15 }}>✓</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{s}</Typography>
          </Box>
        ))}
      </MockupFrame>

      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.5, mb: 1.5 }}>Execution status values</Typography>
      <DocTable headers={["Status", "Meaning"]} rows={[
        [<StatusBadge status="pending" />, "Queued, waiting for the executor to pick it up"],
        [<StatusBadge status="running" />, "Currently executing in the Playwright browser"],
        [<StatusBadge status="passed" />,  "All steps completed successfully"],
        [<StatusBadge status="failed" />,  "One or more steps failed; see screenshot & error message"],
      ]} />
    </Box>
  );
}

function SuitesSection({ onNavigate }) {
  return (
    <Box>
      <SectionHeader eyebrow="Core Feature" title="Test Suites" desc="Test Suites group multiple tests and run them together as a batch. Use suites for smoke tests, full regression runs, or feature-specific verification." />
      <FeatureGrid>
        <FeatureCard title="📋 Create a Suite">Go to <strong>Suites</strong> in the sidebar, click <strong>New Suite</strong>, name it, and add tests from any folder. Tests run in the order you specify.</FeatureCard>
        <FeatureCard title="▶️ Run a Suite">Click <strong>Run Suite</strong> — the backend iterates each test and triggers the executor. Results are aggregated into a single suite execution record.</FeatureCard>
        <FeatureCard title="📊 Suite Results">See a summary: tests passed, tests failed, total duration, and individual test results — all in the Executions log.</FeatureCard>
        <FeatureCard title="🕐 Schedule a Suite">Suites can run on a cron schedule — e.g. smoke tests every 30 minutes.</FeatureCard>
      </FeatureGrid>
      <Callout type="tip" icon="💡"><strong>Recommended structure:</strong> Create a "Smoke Tests" suite with your 5–10 most critical flows. Run it on every deploy. Create a "Full Regression" suite for nightly runs.</Callout>
    </Box>
  );
}

function HealingSection() {
  return (
    <Box>
      <SectionHeader eyebrow="AI Feature" title="Self-Healing AI" desc="When the UI changes and a selector no longer matches, Testify detects the drift, proposes a corrected selector, and lets you apply it in one click." />
      <Callout type="success" icon="🔧"><strong>This is Testify's biggest differentiator.</strong> Teams using traditional tools spend hours each sprint fixing broken selectors after UI updates. Self-healing eliminates this maintenance cost entirely.</Callout>
      <Steps>
        <Step num={1} title="Selector fails during execution">A step can't find its element — a developer changed a CSS class, ID, or attribute. The executor logs the failure along with DOM state at the moment of error.</Step>
        <Step num={2} title="Healing candidate is generated">The system analyzes the DOM context, finds alternative selectors that likely match the same element (using text content, ARIA roles, nearby structure), and creates a HealingEvent with status <StatusBadge status="healing" />.</Step>
        <Step num={3} title="You review and apply">Navigate to the <strong>Healing</strong> section. You'll see each pending event with the old selector, the proposed new selector, and a diff. Click <strong>Apply</strong> to update the test, or <strong>Reject</strong> to handle it manually.</Step>
        <Step num={4} title="Test reruns and passes">After applying, rerun the test to confirm the fix works. The healing event status updates to <StatusBadge status="active" /> and the test continues working on future runs.</Step>
      </Steps>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Healing event statuses</Typography>
      <DocTable headers={["Status", "Meaning"]} rows={[
        [<StatusBadge status="healing" />,  "A candidate fix has been proposed, awaiting your decision"],
        [<StatusBadge status="active" />,   "You approved the fix; the test step has been updated"],
        [<StatusBadge status="rejected" />, "You dismissed the candidate; step remains unchanged for manual editing"],
      ]} />
    </Box>
  );
}

function DataDrivenSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Advanced Feature" title="Data-Driven Testing" desc="Run the same test logic with multiple sets of input data. Upload a CSV, bind columns to test variables, and Testify executes one run per data row — automatically." />
      <FeatureGrid>
        <FeatureCard title="📄 Upload a Dataset">Go to <strong>Datasets</strong>. Upload a CSV — each column becomes a variable, each row becomes one test execution.</FeatureCard>
        <FeatureCard title="🔗 Bind Variables">In the Test Editor use <Code>{"{{columnName}}"}</Code> anywhere in step values. E.g. <Code>{"{{email}}"}</Code> and <Code>{"{{password}}"}</Code> in a login test.</FeatureCard>
        <FeatureCard title="▶️ Run With Data">When running a data-bound test, select a dataset. Testify executes one run per row and shows all results together.</FeatureCard>
        <FeatureCard title="📊 Row-Level Results">See results per data row — which row succeeded, which failed, with full error detail and screenshots per row.</FeatureCard>
      </FeatureGrid>
      <Callout type="tip" icon="💡">Name CSV columns clearly: <Code>email</Code>, <Code>password</Code>, <Code>expected_name</Code>. Then in the editor your Type step value becomes <Code>{"{{email}}"}</Code> and your assertion becomes "Assert text <Code>{"{{expected_name}}"}</Code> is visible". Values are substituted at runtime by the executor.</Callout>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>CSV format requirements</Typography>
      <DocTable headers={["Rule", "Detail"]} rows={[
        ["First row = headers", <>Column names become the variable names used in <Code>{"{{...}}"}</Code> syntax</>],
        ["UTF-8 encoding",      "Required for special characters and international data"],
        ["Quoted values",       <>Use double-quotes around values containing commas: <Code>"Smith, John"</Code></>],
        ["No empty headers",    "Every column must have a non-empty header name"],
      ]} />
    </Box>
  );
}

function SchedulingSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Advanced Feature" title="Scheduling" desc="Schedule tests or suites to run automatically on a cron schedule — every hour, every night at 2am, or after every deploy." />
      <FeatureGrid>
        <FeatureCard title="⏱ Cron Scheduling">Use standard cron syntax. Common schedules are available as presets: hourly, nightly, weekdays only, and more.</FeatureCard>
        <FeatureCard title="📬 Failure Alerts">Get notified when a scheduled run fails via email or webhook so your team is alerted when production tests break.</FeatureCard>
        <FeatureCard title="📋 Schedule History">Every scheduled run is logged in Executions history, so you can spot regressions over time in Analytics.</FeatureCard>
        <FeatureCard title="🔧 Edit or Pause">Schedules can be edited, paused, or deleted at any time. Pausing preserves configuration without deleting it.</FeatureCard>
      </FeatureGrid>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Common cron patterns</Typography>
      <DocTable headers={["Pattern", "Cron expression", "Use case"]} rows={[
        ["Every hour",          <Code>0 * * * *</Code>,    "Production smoke tests"],
        ["Nightly at 2am UTC",  <Code>0 2 * * *</Code>,    "Full regression suite"],
        ["Weekday mornings",    <Code>0 9 * * 1-5</Code>,  "Pre-standup health check"],
        ["Every 30 minutes",    <Code>*/30 * * * *</Code>, "Critical user path monitoring"],
        ["Every Monday 8am",    <Code>0 8 * * 1</Code>,    "Weekly regression report"],
      ]} />
    </Box>
  );
}

function CiCdSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Advanced Feature" title="Run tests from CI/CD" desc="Run a suite (or chosen tests) from GitHub Actions, GitLab CI, Jenkins or Azure DevOps on every push, and fail the build when a test fails." />
      <FeatureGrid>
        <FeatureCard title="🔑 API tokens">Create one in Settings → API tokens and store it as a secret in your CI system. It acts as you and can only start, follow and stop runs.</FeatureCard>
        <FeatureCard title="⌨️ Testify CLI">A single file with no dependencies, downloaded from this server: <Code>/downloads/testify.mjs</Code>. It needs Node.js 18 or newer.</FeatureCard>
        <FeatureCard title="📄 JUnit report">Add <Code>--junit report.xml</Code> and your CI system shows each test's result, with the failing step's error.</FeatureCard>
        <FeatureCard title="🔗 Linked both ways">The CLI prints a link to the run; Suite Runs marks it with a CI chip and links back to the build.</FeatureCard>
      </FeatureGrid>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Set up your pipeline</Typography>
      <PipelineSetup />
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 2.75, mb: 1.5 }}>Exit codes</Typography>
      <DocTable headers={["Code", "Meaning"]} rows={[
        [<Code>0</Code>, "Every test passed"],
        [<Code>1</Code>, "One or more tests failed"],
        [<Code>2</Code>, "The run was stopped or timed out (--timeout, default 60 minutes)"],
        [<Code>3</Code>, "Usage, login or connection error"],
      ]} />
      <Callout type="tip" icon="💡">CI runs wait in the same queue as every other run and count towards your organisation's parallel limit. Cancelling the CI job stops the run in Testify too.</Callout>
    </Box>
  );
}

function AnalyticsSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Advanced Feature" title="Analytics Dashboard" desc="Track pass/fail trends over time, identify flaky tests, and report test quality metrics to stakeholders." />
      <FeatureGrid>
        <FeatureCard title="📉 Pass Rate Trends"><strong>Analytics → Quality</strong> shows the pass rate, runs and average run time per day over the last 7, 30 or 90 days, for all projects or one, compared with the period before.</FeatureCard>
        <FeatureCard title="🎯 Flaky Test Detection">Tests whose result changed 3 or more times are listed as <strong>Flaky</strong> on Analytics → Quality, next to the tests that failed most and the slowest ones.</FeatureCard>
        <FeatureCard title="🏷 What changed in a run">On <strong>Suite Runs</strong>, each test is tagged against its last 10 runs: <strong>New failure</strong>, <strong>Fixed</strong>, <strong>Flaky</strong>, <strong>Always failing</strong> or <strong>Slow</strong>, with dots for its recent results.</FeatureCard>
        <FeatureCard title="📤 Stakeholder Reporting">On Suite Runs, <strong>Export</strong> downloads a run's report as a PDF (with screenshots of failed steps) or CSV, or creates a read-only link that expires after 7 or 30 days, for people without an account.</FeatureCard>
        <FeatureCard title="🔔 Notifications">Suites → ⋮ → <strong>Notifications</strong>: email, Slack or Teams when a run finishes — every run, only failed runs, or only when the result changes. Works for scheduled and CI runs too.</FeatureCard>
      </FeatureGrid>
    </Box>
  );
}

function SearchSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Reference" title="Search" desc="Global search lets you find tests, projects, folders, suites, and datasets across your entire workspace instantly." />
      <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", gap: 1.125, px: 2, py: 1.625, maxWidth: 440, borderRadius: 2, border: `1.5px solid ${PRIMARY}`, boxShadow: `0 4px 20px ${alpha(PRIMARY, .15)}`, mb: 2.25 }}>
        <SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
        <Typography sx={{ fontSize: 14, color: "#94a3b8", flex: 1 }}>Search tests, projects, folders…</Typography>
        <Kbd>/</Kbd>
      </Paper>
      <FeatureGrid>
        <FeatureCard title="⌨️ Keyboard Shortcut">Press <Kbd>/</Kbd> anywhere in the app to open the search bar instantly.</FeatureCard>
        <FeatureCard title="🔎 What's Searchable">Test names, folder names, project names, suite names, and dataset names. Results are ranked by recency and relevance.</FeatureCard>
        <FeatureCard title="🚀 Jump Directly">Click any result to navigate directly to that item — whether it's a test editor, a suite, or an analytics page.</FeatureCard>
        <FeatureCard title="💾 Saved Views">Frequently-used search queries can be saved as <strong>Views</strong> for one-click access. Saved Views are workspace-scoped and shared with your team.</FeatureCard>
      </FeatureGrid>
    </Box>
  );
}

function WorkspaceSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Reference" title="Workspace & Authentication" desc="Testify is multi-tenant — your data is scoped to a Workspace. Each user belongs to one or more workspaces, and projects live inside workspaces." />
      <FeatureGrid>
        <FeatureCard title="🏢 Workspace Isolation">Projects, tests, users, and analytics are fully scoped to the workspace. Switch workspaces from the account menu (top-right).</FeatureCard>
        <FeatureCard title="👤 JWT Authentication">Uses JWT with 30-minute access tokens and 7-day refresh tokens. You'll be automatically re-authenticated on page refresh.</FeatureCard>
        <FeatureCard title="🔐 Session Expiry">If your session expires you'll be redirected to <Code>/login?expired=true</Code>. Simply log back in — you'll return to where you were.</FeatureCard>
        <FeatureCard title="🌱 Demo Mode">Log in with <Code>demo@testify.com</Code> / <Code>demo123</Code> to explore with pre-seeded sample data. Perfect for evaluating or onboarding teammates.</FeatureCard>
      </FeatureGrid>
      <Callout type="warning" icon="⚠️"><strong>Default workspace:</strong> If no <Code>workspaceId</Code> is set, data defaults to the <Code>"default"</Code> workspace. For team setups always configure an explicit workspace ID to keep data separate.</Callout>
    </Box>
  );
}

function ShortcutsSection() {
  return (
    <Box>
      <SectionHeader eyebrow="Reference" title="Keyboard Shortcuts" desc="Speed up your workflow with these keyboard shortcuts." />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25, color: "text.primary" }}>Navigation</Typography>
          <DocTable headers={["Key", "Action"]} rows={[
            [<Kbd>/</Kbd>,   "Open global search"],
            [<Kbd>Esc</Kbd>, "Close modal / cancel"],
            [<Kbd>?</Kbd>,   "Open help panel"],
          ]} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25, color: "text.primary" }}>Test Editor</Typography>
          <DocTable headers={["Key", "Action"]} rows={[
            [<Box sx={{ display: "flex", gap: 0.5 }}><Kbd>Ctrl</Kbd>+<Kbd>S</Kbd></Box>,     "Save test"],
            [<Box sx={{ display: "flex", gap: 0.5 }}><Kbd>Ctrl</Kbd>+<Kbd>Enter</Kbd></Box>, "Run test"],
            [<Box sx={{ display: "flex", gap: 0.5 }}><Kbd>Ctrl</Kbd>+<Kbd>Z</Kbd></Box>,     "Undo last step edit"],
          ]} />
        </Box>
      </Box>
    </Box>
  );
}

function GlossarySection() {
  const terms = [
    ["Test",          "A recorded sequence of browser interactions (steps) that can be replayed to verify application behaviour."],
    ["Step",          "A single action within a test — e.g. Navigate, Click, Type, Assert."],
    ["Assertion",     'A step that verifies something is true about the page — e.g. "text X is visible". Assertions are what make tests meaningful.'],
    ["Selector",      <>A CSS selector or XPath used to locate an element in the page DOM — e.g. <Code>#email</Code>, <Code>button[type=submit]</Code>.</>],
    ["Selector drift", "When a selector no longer matches its intended element because the UI was updated — the most common cause of test failures."],
    ["Project",       "Top-level container for related tests. Usually maps to one application or product."],
    ["Folder",        "Organizational unit within a project. Groups tests by feature, team, or sprint."],
    ["Suite",         "An ordered collection of tests that run together as a batch with a shared result."],
    ["Execution",     "One run of a test or suite, producing a PASSED/FAILED result with per-step details."],
    ["HealingEvent",  "A record created when self-healing detects selector drift. Has statuses PENDING_REVIEW, APPLIED, REJECTED."],
    ["Dataset",       <>A CSV file uploaded to Testify. Columns become variables (<Code>{"{{col}}"}</Code>) substituted into test steps at runtime.</>],
    ["Workspace",     "A multi-tenant container scoping all data (projects, tests, users) for one team or organisation."],
    ["Executor",      "The Node.js + Playwright service (port 3001) that actually runs tests in a real browser and streams results via Socket.io."],
    ["Flaky test",    "A test that alternates between passing and failing without code changes — usually due to timing issues or unstable selectors."],
  ];
  return (
    <Box>
      <SectionHeader eyebrow="Reference" title="Glossary" desc="Key terms used throughout Testify." />
      <DocTable headers={["Term", "Definition"]} rows={terms.map(([term, def]) => [<strong>{term}</strong>, def])} />
    </Box>
  );
}

function FAQSection() {
  const faqs = [
    ["Why does the extension only work on certain pages?", <>The recorder bridge (<Code>recorder-bridge.js</Code>) is only injected on <Code>localhost:3000</Code> and <Code>*.testify.com</Code>. This is intentional to prevent accidental recording on other sites. Your target app is recorded separately via the content script running on all tabs.</>],
    ["My test passed yesterday but fails today — what happened?", "Usually selector drift — a developer changed a CSS class, button label, or DOM structure. Check the Healing section: Testify may have already identified the change and proposed a fix. If not, use the failure screenshot to identify what changed and edit the step manually."],
    ['How do I test behind a login wall?', 'Record the login steps as the first part of your test, or create a shared "Login" test and prepend its steps to tests that need an authenticated session. Data-driven testing lets you parameterize credentials so you can test multiple accounts from a single test definition.'],
    ["Can multiple people use Testify at the same time?", "Yes. Testify is multi-user and multi-tenant. Tests, projects, and suites are shared within the same workspace so team members can collaborate simultaneously."],
    ["Does Testify work with React, Vue, and other SPA frameworks?", <>Yes. Playwright handles JavaScript-heavy SPAs natively — it waits for network requests, animations, and DOM updates before proceeding to the next step. Far more reliable than older tools that required explicit <Code>sleep()</Code> calls.</>],
    ["What's the difference between a Test, Suite, and Schedule?", <><strong>Test</strong> = a single recorded flow. <strong>Suite</strong> = an ordered collection of tests run together. <strong>Schedule</strong> = a time-based trigger that automatically runs a test or suite on a cron pattern (e.g. every night at 2am).</>],
    ["The executor isn't running — how do I start it?", <><Code>cd executor && npm install && npx playwright install && npm start</Code>. The executor listens on port 3001. For first-time setup, <Code>npx playwright install</Code> downloads the Chromium browser binary — this takes a few minutes. Without the executor running, test execution will fail silently.</>],
    [<>How do I use <Code>{"{{variables}}"}</Code> in test steps?</>, <>Upload a CSV dataset under <strong>Datasets</strong>. Column names become variable names. In the Test Editor, use <Code>{"{{columnName}}"}</Code> anywhere in a step value. When you run the test, select the dataset and Testify substitutes values row by row.</>],
  ];
  return (
    <Box>
      <SectionHeader eyebrow="Reference" title="Frequently Asked Questions" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {faqs.map(([q, a], i) => (
          <Accordion key={i} variant="outlined" sx={{ borderRadius: "8px !important", "&:before": { display: "none" }, "&.Mui-expanded": { mt: 0 } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ "& .MuiAccordionSummary-content": { my: 1.5 } }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: 13.5, color: "text.secondary", lineHeight: 1.65 }}>{a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <Box sx={{ pt: 4.5, pb: 2, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.25, mt: 2.5 }}>
        <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>Testify Documentation · April 2026</Typography>
        <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>Questions? <Box component="a" href="mailto:support@testify.io" sx={{ color: PRIMARY }}>support@testify.io</Box></Typography>
      </Box>
    </Box>
  );
}

/* ─── Main Help page ──────────────────────────────────────── */
export default function Help() {
  const [activeSection, setActiveSection] = useState(() => sessionStorage.getItem(SECTION_KEY) || "welcome");
  const [searchQuery, setSearchQuery] = useState("");
  const [checkedItems, setCheckedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CHECKLIST_KEY) || "[]"); }
    catch { return []; }
  });

  const navigate = (id) => {
    setActiveSection(id);
    setSearchQuery("");
    sessionStorage.setItem(SECTION_KEY, id);
  };

  const toggleChecklist = (id) => {
    setCheckedItems(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const groups = [...new Set(SECTIONS.map(s => s.group))];
  const filteredSections = searchQuery.trim()
    ? SECTIONS.filter(s => s.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : SECTIONS;

  const renderContent = () => {
    switch (activeSection) {
      case "welcome":    return <WelcomeSection onNavigate={navigate} />;
      case "checklist":  return <ChecklistSection checkedItems={checkedItems} onToggle={toggleChecklist} />;
      case "quickstart": return <QuickStartSection />;
      case "extension":  return <ExtensionSection />;
      case "projects":   return <ProjectsSection />;
      case "recording":  return <RecordingSection />;
      case "editor":     return <EditorSection />;
      case "execution":  return <ExecutionSection />;
      case "suites":     return <SuitesSection onNavigate={navigate} />;
      case "healing":    return <HealingSection />;
      case "datadriven": return <DataDrivenSection />;
      case "scheduling": return <SchedulingSection />;
      case "cicd":       return <CiCdSection />;
      case "analytics":  return <AnalyticsSection />;
      case "search":     return <SearchSection />;
      case "workspace":  return <WorkspaceSection />;
      case "shortcuts":  return <ShortcutsSection />;
      case "glossary":   return <GlossarySection />;
      case "faq":        return <FAQSection />;
      default:           return <WelcomeSection onNavigate={navigate} />;
    }
  };

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)", overflow: "hidden" }}>
      {/* ── Help nav panel ── */}
      <Box sx={{ width: 252, minWidth: 252, bgcolor: "background.paper", borderRight: 1, borderColor: "divider", overflowY: "auto", display: "flex", flexDirection: "column", pb: 5 }}>
        <Box sx={{ px: 1.5, pt: 1.75, pb: 1.25 }}>
          <TextField
            size="small"
            placeholder="Search docs…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
            fullWidth
          />
        </Box>

        {searchQuery.trim() ? (
          <Box sx={{ px: 1 }}>
            {filteredSections.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: "#94a3b8", px: 1.5, py: 1 }}>No results found</Typography>
            ) : filteredSections.map(s => (
              <Box key={s.id} onClick={() => navigate(s.id)} sx={{ display: "flex", alignItems: "center", gap: 1.125, px: 1.5, py: 0.875, borderRadius: 0.875, fontSize: 13.5, fontWeight: 500, color: activeSection === s.id ? "primary.main" : "text.secondary", bgcolor: activeSection === s.id ? PRIMARY_LIGHT : "transparent", cursor: "pointer", mb: 0.25, "&:hover": { bgcolor: activeSection === s.id ? PRIMARY_LIGHT : "action.hover", color: activeSection === s.id ? "primary.main" : "text.primary" }, "& .MuiSvgIcon-root": { color: activeSection === s.id ? PRIMARY : "#94a3b8" } }}>
                {s.icon}<Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: activeSection === s.id ? 600 : 500 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          groups.map(group => (
            <Box key={group}>
              <Typography sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "#94a3b8" }}>{group}</Typography>
              {SECTIONS.filter(s => s.group === group).map(s => (
                <Box key={s.id} onClick={() => navigate(s.id)} sx={{ display: "flex", alignItems: "center", gap: 1.125, mx: 1, px: 1.5, py: 0.875, borderRadius: 0.875, cursor: "pointer", mb: 0.25, bgcolor: activeSection === s.id ? PRIMARY_LIGHT : "transparent", "&:hover": { bgcolor: activeSection === s.id ? PRIMARY_LIGHT : "action.hover", color: activeSection === s.id ? "primary.main" : "text.primary" }, "& .MuiSvgIcon-root": { color: activeSection === s.id ? PRIMARY : "#94a3b8", fontSize: 16 } }}>
                  {s.icon}
                  <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: activeSection === s.id ? 600 : 500, color: activeSection === s.id ? "primary.main" : "text.secondary" }}>{s.label}</Typography>
                  {s.badge && (
                    <Chip label={s.badge} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25, bgcolor: s.badgeColor === "secondary" ? "#fdf4ff" : EMERALD_LIGHT, color: s.badgeColor === "secondary" ? VIOLET : "#065f46", "& .MuiChip-label": { px: 0.875 } }} />
                  )}
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>

      {/* ── Content area ── */}
      <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "background.default" }}>
        <Box sx={{ maxWidth: 900, mx: "auto", px: 5, py: 4.5 }}>
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
}
